import React, {useState, useEffect} from 'react'
import {
  DndContext,
  closestCenter,
  TouchSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {toAbsoluteUrl} from '../../../../_gori/helpers'
import {useIntl} from 'react-intl'

interface SortableListInputProps {
  list: string[]
  name: string
  formik: any
}

const SortableItem: React.FC<{id: string; index: number}> = ({id, index}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id})

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? '#f8f9fa' : '#ffffff',
    userSelect: 'none' as const,
  }

  const intl = useIntl()
  const label = id

  return (
    <li
      ref={setNodeRef}
      style={style}
      className='d-flex align-items-center justify-content-between py-2 px-3 mb-2 border rounded w-100'
      {...attributes}
      {...listeners}
    >
      <span className='fw-bold me-3 fs-6'>{index + 1}.</span>
      <span className='flex-grow-1 fs-6 text-truncate'>{label}</span>
      <img
        src={toAbsoluteUrl('/media/gori/surveys/grab_n_move.svg')}
        alt={intl.formatMessage({id: 'GRAB_ICON_ALT'})}
        width={24}
        height={24}
      />
    </li>
  )
}

const SortableListInput: React.FC<SortableListInputProps> = ({list, name, formik}) => {
  const intl = useIntl()
  const [items, setItems] = useState<string[]>([])

  useEffect(() => {
    const saved = formik.values[name]
    if (Array.isArray(saved) && saved.length) {
      setItems(saved)
    } else {
      const defaults = list.map((id) => intl.formatMessage({id}))
      setItems(defaults)
      formik.setFieldValue(name, defaults)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sensors = useSensors(
    useSensor(TouchSensor, {activationConstraint: {delay: 200, tolerance: 5}}),
    useSensor(PointerSensor, {activationConstraint: {distance: 5}})
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event
    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id as string)
      const newIndex = items.indexOf(over.id as string)
      const newOrder = arrayMove(items, oldIndex, newIndex)
      setItems(newOrder)
      formik.setFieldValue(name, newOrder)
    }
  }

  return (
    <div className='w-100' style={{touchAction: 'none'}}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <ul className='list-unstyled p-0 m-0'>
            {items.map((id, idx) => (
              <SortableItem id={id} index={idx} key={id} />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      <input type='hidden' name={name} value={JSON.stringify(items)} />
      {formik.submitCount > 0 && formik.errors[name] && (
        <div className='text-danger mt-2'>{formik.errors[name]}</div>
      )}
    </div>
  )
}

export default SortableListInput
